import type { PublicCountryConfig } from './types.js';

// KZ ru-only to start: goszakup.gov.kz and other KZ sources publish region names in
// Russian and the site renders ru, so localize is a passthrough (unlike UZ, which maps
// Uzbek-Latin source names to ru). The 17 oblasts + 3 republican-significance cities
// (Astana/Almaty/Shymkent; Baikonur excluded — special status) live in the DB regions
// table, seeded per deployment; the config only needs to localize a raw name.
export const kzPublic: PublicCountryConfig = {
  code: 'kz',
  // ru (default, bare paths) + kk (Kazakh, /kk/* prefixed). The Kazakh catalog is
  // a machine-translation first pass pending native review; the ru↔kk switcher
  // renders because LOCALES.length > 1.
  locales: ['ru', 'kk'],
  defaultLocale: 'ru',
  regions: {
    localize(name, _locale) {
      return name && name.trim() ? name : null;
    },
  },
  currency: {
    code: 'KZT',
    symbol: '₸',
    // Same order/shape as uz.public.ts's ru array so format.ts picks it by locale key
    // with no country branch. (uz ru = ['трлн','млрд','млн','тыс.'].)
    compactUnits: {
      ru: ['трлн', 'млрд', 'млн', 'тыс.'],
    },
    // KZ amounts read «тенге» (KZT), the ru-locale currency word — parallel to
    // UZ's «сум». (₸/тг are alternatives; the word matches the UZ UI convention.)
    // kk = Kazakh «теңге».
    words: { ru: 'тенге', kk: 'теңге' },
  },
  brand: {
    name: 'KazTender.com',
    siteUrl: 'https://kaztender.com',
    supportEmail: 'support@kaztender.com',
    // Owner's call 2026-10-13: contacts on KZ are Telegram and email ONLY — the
    // phone / WhatsApp line (his own Uzbek +998 mobile) is not published here.
    // The Telegram contact is the same support handle the UZ site publishes;
    // change it on this one line if KZ gets its own account.
    supportTelegram: { handle: '@flesteyto', url: 'https://t.me/flesteyto' },
    publishPhone: false,
  },
  // Country name in the cases the copy uses — ru + kk (Kazakh).
  countryName: {
    ru: { nom: 'Казахстан', gen: 'Казахстана', prep: 'Казахстане' },
    kk: { nom: 'Қазақстан', gen: 'Қазақстанның', prep: 'Қазақстанда' },
  },
  // Web ИНН/БИН form: KZ БИН/ИИН is 12 digits (server keeps the full checksum validate).
  companyIdForm: { label: 'БИН/ИИН', digits: 12 },
  phonePlaceholder: '+7 707 123 45 67',
  // KZ's own Meta Pixel — a SEPARATE pixel from UZ's (provided 2026-09-16). Its
  // CAPI access token is a server secret in the KZ host .env, not here.
  // ga4MeasurementId '' = GA4 OFF on KZ (no leak into UZ's property; KZ gets its
  // own GA4 later, or stays off).
  analytics: { metaPixelId: '1612532190234629', ga4MeasurementId: '' },
  // KZ runs its own bot since 2026-10-14 (owner): @kaztender_com_bot, whose
  // assistant is Айгерим. Every Telegram surface the Uzbek site has (login
  // widget, connect banners, onboarding step, alerts in the chat) is on. There
  // is NO Kazakh channel, so channel lines and footers render nothing here. The
  // first alert of a search saved before any chat is linked still goes by
  // e-mail — the channel KZ accounts had until now (see MessagingConfig).
  messaging: {
    telegram: true,
    telegramBot: 'kaztender_com_bot',
    telegramChannel: null,
    emailFirstAlert: true,
  },
  // priceIntel ON (2026-09-18): goszakup publishes КТРУ codes that flow through the
  // classifier stub, so ~81% of live KZ lots carry an ikpu + procurement_kind — enough
  // for /market, the goods/works/services kind filter, and an /prices band built from
  // goszakup contract award prices (the goszakup-contracts registry harvest; BidHistory
  // stays UZ-only). teamPlans ON since 2026-10-13 (owner): Kazakhstan sells Pro and
  // «Бизнес», monthly — see `payments` below for what is NOT sold.
  // corporateContactGate OFF: KZ's corporate sources (mitwork.kz; Samruk-Kazyna from the
  // zakup.gov.kz single window) publish a buyer БИН and no contact, so gating corporate on
  // a contact would hide the whole layer (~1,381 mitwork lots then). Exempt it like
  // ifi/supply — the value is the notice + AI summary, not a contact card.
  // conversionLandings OFF (2026-10-13): /start and /ip exist only as Uzbek copy —
  // «Все тендеры Узбекистана», prices in so'm, Payme, the +998 phone — and were
  // being served, prerendered and sitemapped on kaztender.com. Off until KZ has
  // landing content of its own; the paths redirect to the catalogue meanwhile.
  features: {
    priceIntel: true,
    teamPlans: true,
    corporateContactGate: false,
    conversionLandings: false,
    // ИИ-онбординг ON for everyone (owner, 2026-10-15: «ИИ-онбординг врубай на
    // КЗ») — no PostHog flag, no A/B: the onboarding search step asks «Что вы
    // поставляете?» and the server proposes a saved search from KZ's own
    // categories, niches, ЕНС ТРУ codes and regions.
    aiOnboarding: true,
    // Owner, 2026-10-15: a Pro teaser shows only what Pro then shows.
    honestTeasers: true,
    // No supply-request source in KZ `sourceKeys` → no «Кооперация» chip.
    supplyScope: false,
  },
  // Owner's decisions, 2026-10-13. Kazakhstan pays through Stripe: one button per
  // plan that leaves for Stripe Checkout (a monthly subscription in tenge) — the
  // Uzbek card sheet, the gateway chooser and the single payment are never
  // opened here. MONTHLY ONLY for now (no «Год» tab), no payment by invoice, no
  // ИП tier: the two plans sold are Pro and «Бизнес». The only marks that may be
  // printed are Visa, Mastercard and Stripe. Cards are accepted by the owner's
  // US company; the operator in the requisites stays the Uzbek one.
  payments: {
    checkout: 'stripe',
    yearlyPlans: false,
    bankInvoice: false,
    ipPlan: false,
    marks: ['visa', 'mastercard', 'stripe'],
    cardPayee: {
      name: 'AI CRM Inc',
      address: '251 Little Falls Drive, Wilmington, New Castle County, DE 19808, USA',
      phone: '302-636-5401',
      processor: 'Stripe',
    },
  },
};
